import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../context/LanguageContext";
import { Zap, Users, ChevronLeft, History as HistoryIcon, Star, Sparkles } from "lucide-react";

export default function UflHub() {
  const { t } = useT();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ufl/state");
      setState(data);
    } catch (e) {
      console.error(e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cred = state?.credibility;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#0F172A] to-[#020617] text-white pb-20" data-testid="v2-ufl-hub">
      <div className="px-4 pt-5 pb-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#60A5FA] text-[#020617] text-[9px] font-extrabold uppercase tracking-[0.3em]">
            <Sparkles className="w-3 h-3" /> UFL · Mobile
          </span>
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="v2-ufl-hub-back"
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> {t("ufl.hub.back")}
          </button>
        </div>
        <div className="relative overflow-hidden rounded-3xl bg-[#020617] border border-[#60A5FA]/30">
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#60A5FA]/20 blur-[140px]" />
            <div className="absolute -bottom-24 right-0 w-72 h-72 rounded-full bg-emerald-500/15 blur-[140px]" />
          </div>
          <div className="relative px-5 py-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#60A5FA]">{t("ufl.hub.kicker")}</div>
            <div className="font-heading text-2xl uppercase tracking-wider leading-none mt-1">{t("ufl.hub.title")}</div>
            <div className="text-[11px] text-white/60 mt-1">{t("ufl.hub.sub")}</div>
          </div>
        </div>

        {/* Credibility strip */}
        <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 flex items-center justify-between" data-testid="v2-ufl-credibility">
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{t("ufl.hub.credibility")}</div>
            <div className="text-xl font-extrabold text-[#FFD700] tabular-nums">
              {cred?.score == null ? "—" : `${cred.score.toFixed(2)}`}
              <span className="text-white/40 text-sm font-mono ml-1">/ 5</span>
            </div>
            <div className="text-[10px] font-mono text-white/40">{t("ufl.hub.gamesRated", { n: cred?.count ?? 0 })}</div>
          </div>
          <Star className="w-8 h-8 text-[#FFD700]" />
        </div>
      </div>

      {/* 2x2 widget — Quick Play + Teams */}
      <div className="px-4 grid grid-cols-2 gap-3">
        <Link
          to="/v2/ufl/quickplay"
          data-testid="v2-ufl-hub-quickplay"
          className="group relative rounded-3xl bg-gradient-to-br from-[#10B981]/15 via-[#0F172A] to-[#020617] border border-[#10B981]/40 hover:border-[#10B981] transition-all aspect-square overflow-hidden"
        >
          <div className="absolute inset-0 p-4 flex flex-col">
            <div className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#10B981]">// 01</div>
            <Zap className="w-10 h-10 text-[#10B981] mt-2 drop-shadow-[0_0_20px_rgba(16,185,129,0.6)]" />
            <div className="mt-auto">
              <div className="font-heading text-base sm:text-lg uppercase tracking-wide leading-tight">
                {t("ufl.hub.quickPlay.title")}
              </div>
              <div className="text-[10px] text-white/60 mt-1 leading-snug">
                {t("ufl.hub.quickPlay.sub")}
              </div>
            </div>
            {state?.in_queue && (
              <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-[#020617] text-[8px] font-extrabold uppercase tracking-widest animate-pulse">
                in queue
              </span>
            )}
            {state?.active_room && (
              <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#10B981] text-white text-[8px] font-extrabold uppercase tracking-widest" data-testid="v2-ufl-hub-active-room-pill">
                live room
              </span>
            )}
          </div>
        </Link>

        <Link
          to="/v2/ufl/teams"
          data-testid="v2-ufl-hub-teams"
          className="group relative rounded-3xl bg-gradient-to-br from-[#7C3AED]/15 via-[#0F172A] to-[#020617] border border-[#7C3AED]/40 hover:border-[#7C3AED] transition-all aspect-square overflow-hidden"
        >
          <div className="absolute inset-0 p-4 flex flex-col">
            <div className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#A5B4FC]">// 02</div>
            <Users className="w-10 h-10 text-[#A5B4FC] mt-2 drop-shadow-[0_0_20px_rgba(165,180,252,0.6)]" />
            <div className="mt-auto">
              <div className="font-heading text-base sm:text-lg uppercase tracking-wide leading-tight">
                {t("ufl.hub.teams.title")}
              </div>
              <div className="text-[10px] text-white/60 mt-1 leading-snug">
                {t("ufl.hub.teams.sub")}
              </div>
            </div>
            <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFD700] text-[#020617] text-[8px] font-extrabold uppercase tracking-widest shadow-lg" data-testid="v2-ufl-hub-teams-premium-pill">
              {t("ufl.hub.teams.premium")}
            </span>
          </div>
        </Link>
      </div>

      <div className="px-4 mt-3">
        <Link
          to="/v2/ufl/history"
          data-testid="v2-ufl-hub-history"
          className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-white/60 hover:text-white"
        >
          <HistoryIcon className="w-3.5 h-3.5" /> {t("ufl.hub.history")}
        </Link>
      </div>
    </div>
  );
}
